/**
 * One source of brand truth.
 *
 * UIKit deliberately ships no <Logo>, no <Header> and no <Footer> — product
 * identity is the one thing a component library cannot get right for you.
 * It lives here instead, so there is exactly one place to change it.
 *
 * The theme id must match the class in `styles/brand.css`.
 */
export const brand = {
  /** Theme id — matches `.theme-bloomneo` in styles/brand.css. */
  themeId: 'bloomneo',

  name: 'Bloomneo',
  /** What the product is, in the footer's brand column. */
  tagline: 'Managed server infrastructure, run for you.',

  /**
   * The footer's own sentence about the product.
   *
   * A footer is often the first place a reader lands after scrolling past
   * everything else, and a lockup with nothing under it tells them nothing. Kept
   * to two sentences - anything longer is a page, not a footer.
   */
  blurb: 'We run the servers, databases, storage, backups, security and monitoring behind '
    + 'your software — patched, watched and restored by people who do this every day. '
    + 'One team, one plan, nothing for you to keep alive.',

  /**
   * The framework this is built on, credited once in the footer bar.
   *
   * A link rather than a line of text: it is the only part of the footer that
   * leaves the site, and somebody reading it is looking for the thing itself.
   */
  builtWith: { label: 'Built by Bloom Framework', href: 'https://dev.bloomneo.com' },

  /** Shown in the shell and the browser tab. */
  shortName: 'Bloomneo',

  /**
   * The strip above the copyright line.
   *
   * Not a duplicate of the Legal column by accident. Data-protection notices are
   * expected to be findable without hunting, and the bottom of the page is where
   * people look for them - the column above is for browsing, this is for the
   * reader who arrived with a question about their data.
   */
  compliance: [
    { label: 'Privacy policy', to: '/privacy' },
    { label: 'Cookie policy', to: '/cookies' },
    { label: 'Terms of service', to: '/terms' },
    { label: 'Service level', to: '/sla' },
  ] as { label: string; to: string }[],

  /** Says what is true today: session cookies only, so there is nothing to consent to. */
  complianceNote: 'Essential cookies only — no advertising or third-party tracking.',

  /**
   * Who sells it. The copyright line names the seller, not just the product.
   *
   * A person, because that is who the seller is. The site previously named a
   * company, which made the footer, the Terms and the privacy policy all
   * assert an entity that does not exist — an inaccuracy in its own right,
   * and one a merchant-of-record review checks directly against the name on
   * the application.
   *
   * No href: there is nowhere for a person's name to link to, and pointing it
   * at another business would put the reference straight back.
   */
  company: { label: 'Krishna Teja GS' } as { label: string; href?: string },

  /**
   * Primary navigation, shown in the header and the mobile slide-over.
   *
   * Deliberately few items. A header is a place to put the two or three things
   * a visitor is most likely to want, not an index of the site — that is what
   * the footer and /sitemap are for.
   *
   * /pricing is listed again. It was removed on the reasoning that a public
   * number makes the conversation harder — but the question arrived anyway,
   * every time, and answering it only in a thread meant the figure lived in
   * nobody's head twice the same way. The page now reads the plan rows the
   * admin edits, so publishing a price and changing one are the same act.
   */
  nav: [
    { label: 'Pricing', to: '/pricing' },
    { label: 'Use cases', to: '/usecases' },
    /* The framework this runs on, which is a product of its own. `external`
       because it leaves the site: a router Link would try to match it as a
       route and land on a 404. */
    { label: 'Bloom Framework', to: 'https://dev.bloomneo.com', external: true },
    { label: 'Blog', to: '/blog' },
    { label: 'About', to: '/about' },
    { label: 'Contact', to: '/contact' },
  ] as { label: string; to: string; external?: boolean }[],

  /**
   * Footer link groups — where the rest of the site lives.
   *
   * Every entry here is a route that exists. That sounds like a low bar, and it
   * is exactly the bar the old sitemap page failed: it listed nine pages that
   * had never been built and sent anyone who clicked them to a 404.
   */
  footer: [
    {
      heading: 'Product',
      links: [
        { label: 'Use cases', to: '/usecases' },
        { label: 'Service level', to: '/sla' },
        { label: 'Bloom Framework', to: 'https://dev.bloomneo.com', external: true },
      ],
    },
    {
      heading: 'Company',
      links: [
        { label: 'About', to: '/about' },
        { label: 'Partners', to: '/partners' },
        { label: 'Blog', to: '/blog' },
        { label: 'Contact', to: '/contact' },
      ],
    },
    {
      heading: 'Legal',
      links: [
        { label: 'Privacy', to: '/privacy' },
        { label: 'Terms', to: '/terms' },
        { label: 'Cookies', to: '/cookies' },
        { label: 'Refunds', to: '/refund' },
        { label: 'Cancellation', to: '/cancellation' },
        { label: 'Sitemap', to: '/sitemap' },
      ],
    },
  ] as { heading: string; links: { label: string; to: string; external?: boolean }[] }[],

  social: {
    github: 'https://github.com',
  },
} as const;

export type BrandNavItem = (typeof brand.nav)[number];
